// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Organization } from '@/account/api/types';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { CloseRounded as CloseIcon } from '@/constants/hardfin-standard-icons';
import { QuickBooksOnlineIcon } from '@/icons/quickbooks-online-icon';
import { findIntegrationConfig } from '@/utilities/integration';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface Props {
  organization: Organization;
  isOpen: boolean;
  onClose: () => void;
}

export function QuickBooksConnectionDialog(props: Props) {
  const [isConnected, setIsConnected] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const queryClient = useQueryClient();

  const hasNoAccountingConnection =
    findIntegrationConfig(
      props.organization.integrationConfigurations,
      'QBO'
    ) === null;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== import.meta.env.VITE_API_URL) {
        return;
      }
      try {
        const data = event.data;
        if (data.integration !== 'QBO') {
          return;
        }
        if (data.success) {
          setIsConnected(true);
          setErrorMessage('');
          // Invalidate the organization query to refresh the connection state
          queryClient.invalidateQueries({
            queryKey: ['organization', props.organization.key],
          });
        } else {
          setErrorMessage(
            data.error || 'Unknown error during QuickBooks connection'
          );
        }
      } catch (err) {
        console.error('Failed to parse QuickBooks connection message:', err);
        setErrorMessage('Could not process QuickBooks connection response');
      } finally {
        // Don't continue bubbling this event
        event.stopPropagation();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [queryClient, props.organization.key]);

  const handleClose = () => {
    setErrorMessage('');
    setIsConnected(false);
    props.onClose();
  };

  const handleConnectQuickBooks = () => {
    setErrorMessage('');
    setIsConnected(false);

    // Open popup window for OAuth flow
    const authUrl = `${import.meta.env.VITE_API_URL}/auth/quickbooks/connect`;
    const popup = window.open(
      authUrl,
      'quickbooksConnect',
      'width=720,height=700,toolbar=no,location=yes,status=no,menubar=no,scrollbars=yes,resizable=no'
    );

    if (!popup) {
      setErrorMessage(
        'Could not open QuickBooks connection window. Please ensure popups are allowed.'
      );
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        sx: {
          width: '50.25rem',
          maxWidth: '90vw',
          minWidth: '45rem',
          maxHeight: '90vh',
        },
      }}
      maxWidth={false}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <DialogTitle>Connect accounting system</DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{ mr: 1 }}
          aria-label="close dialog"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers>
        <SupportErrorBoundary message="Something went wrong">
          {isConnected ? (
            <Alert severity="success">
              Successfully connected to QuickBooks Online
            </Alert>
          ) : (
            <Typography>
              The <b>{props.organization.name}</b> organization does not have an
              accounting system configured.
            </Typography>
          )}
          {errorMessage !== '' && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {!isConnected && (
            <Alert severity="info" sx={{ marginY: 2 }}>
              <AlertTitle>QuickBooks admin required</AlertTitle>
              You must be an administrator in your QuickBooks Online account in
              order to connect the integration.
            </Alert>
          )}

          {!isConnected && (
            <Box
              display="grid"
              gridTemplateColumns=".25fr .5fr .25fr"
              justifyContent="center"
              columnGap={1}
              rowGap={2}
              marginY={2}
              alignItems="center"
            >
              <Box />
              <Button
                size="large"
                variant="outlined"
                startIcon={<QuickBooksOnlineIcon fontSize="large" />}
                sx={{ justifyContent: 'center', whiteSpace: 'nowrap' }}
                onClick={handleConnectQuickBooks}
                disabled={isConnected}
              >
                Connect QuickBooks Online
              </Button>
              <Box />
            </Box>
          )}

          {hasNoAccountingConnection && (
            <Typography>
              Review the{' '}
              <Link href="https://guide.hardfin.com/manage-your-account/integrations">
                QuickBooks Online integration documentation
              </Link>{' '}
              or{' '}
              <Link href="mailto:support@hardfin.com">
                contact Hardfin support
              </Link>{' '}
              with questions.
            </Typography>
          )}
        </SupportErrorBoundary>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
